import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getAdditionalReportList(
    $filter: additionalReportFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getAdditionalReportList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        id
        reportName
        fileUrl
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.AdditionalReportListState = {
  result: null,
  error: null,
  loading: false
};

export const getAdditionalReportListRequest = createAsyncThunk<
  TYPES.AdditionalReportList,
  {
    filter?: TYPES.AdditionalReportFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'additional_report/get_additional_report_list',
  async (
  params: {
    filter?: TYPES.AdditionalReportFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getAdditionalReportList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getAdditionalReportListSlice = createSlice({
  name: 'get_additional_report_list',
  initialState: initialState,
  reducers: {
    getAdditionalReportListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getAdditionalReportListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(
      getAdditionalReportListRequest.fulfilled,
      (state, action) => {
        state.result = action.payload;
        state.loading = false;
      }
    ).
    addCase(getAdditionalReportListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getAdditionalReportListClear } =
getAdditionalReportListSlice.actions;

export const getAdditionalReportList = createSelector(
  (state: RootState) =>
  state.additionalReport.getAdditionalReportList.result,
  (state: RootState) =>
  state.additionalReport.getAdditionalReportList.error,
  (state: RootState) =>
  state.additionalReport.getAdditionalReportList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getAdditionalReportListSlice.reducer;